import { Grid, Link as MuiLink, makeStyles } from '@material-ui/core'
import { ContactSupport } from '@material-ui/icons'
import TagIcon from '@material-ui/icons/LocalOffer'
import { Alert } from '@material-ui/lab'
import { GetStaticProps } from 'next'
import { useRouter } from 'next/router'
import React, { useState } from 'react'

import ButtonLink from '@aletheia/common/components/ButtonLink'
import FuzzySearch from '@aletheia/common/components/FuzzySearch'
import HR from '@aletheia/common/components/HR'
import { ShowIntercomButton } from '@aletheia/common/components/Intercom'
import Page from '@aletheia/common/components/Page'

import {
  IFaq,
  fetchSeries,
  isFaq,
  isEntry,
  fetchTags,
  ITag,
} from '../../contentful'

function getFaqsByTags(faqs: IFaq[]): Record<string, IFaq[]> {
  const faqsByTags: Record<string, IFaq[]> = {}
  for (const faq of faqs) {
    const { tags } = faq.fields
    if (!tags) continue
    for (const tag of tags.filter(isEntry)) {
      const { slug } = tag.fields
      faqsByTags[slug] = faqsByTags[slug] || []
      faqsByTags[slug].push(faq)
    }
  }
  return faqsByTags
}

const useStyles = makeStyles((theme) => ({
  faqCollection: {
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  faqEntryListItem: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  tagEntryTitle: {
    marginLeft: theme.spacing(1),
    display: 'inline-block',
  },
  tagEntryCount: {
    display: 'inline-block',
    marginLeft: theme.spacing(1),
    color: theme.palette.primary.dark,
  },
  chatMessage: {
    padding: theme.spacing(3),
  },
  chatMessageInfo: {
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'row-reverse',
    },
  },
  chatMessageIcon: {
    fontSize: '3rem',
    marginRight: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      marginLeft: theme.spacing(3),
    },
  },
}))

type FAQPageProps = {
  FAQs: IFaq[]
  Tags: ITag[]
}

const FAQPage: React.FC<FAQPageProps> = ({ FAQs, Tags }) => {
  const classes = useStyles()
  const faqsByTags = getFaqsByTags(FAQs)
  const { push } = useRouter()
  const [showIntercomError, setShowIntercomError] = useState(false)

  function handleSelect(item: IFaq) {
    push(`/help/[id]`, `/help/${item.sys.id}`)
  }

  return (
    <Page title="EA Funds Help Center" variant="wide">
      {showIntercomError && (
        <Alert severity="error" onClose={() => setShowIntercomError(false)}>
          Could not load chat. This may because you are using a content blocker.
          If you would like to email us directly, you can do so at{' '}
          <MuiLink href="mailto:funds@effectivealtruism.org">
            funds@effectivealtruism.org
          </MuiLink>
        </Alert>
      )}
      <Grid container justifyContent="center">
        <Grid item xs={12} md={8} lg={6}>
          <FuzzySearch
            list={FAQs}
            options={{ keys: ['fields.title', 'fields.body'] }}
            formatResults={(item) => item.fields.title}
            keyBy="sys.id"
            fullWidth
            onSelectItem={handleSelect}
          />
        </Grid>
      </Grid>
      <HR />
      <Grid container spacing={3} justifyContent="center">
        {Object.entries(faqsByTags)
          .map(([tagSlug, FAQs]) => {
            const Tag = Tags.find((Tag) => Tag.fields.slug === tagSlug)
            if (!Tag) return null
            return (
              <Grid item key={Tag.sys.id} xs={12} sm={6} md={4}>
                <ButtonLink
                  href={`/help/tag/[slug]`}
                  as={`/help/tag/${tagSlug}`}
                  size="large"
                  fullWidth
                  variant="outlined"
                  color="primary"
                >
                  <TagIcon />
                  <span className={classes.tagEntryTitle}>
                    {Tag.fields.title}
                  </span>
                  <span className={classes.tagEntryCount}>({FAQs.length})</span>
                </ButtonLink>
              </Grid>
            )
          })
          .filter((a) => a)}
        <Grid item xs={12} sm={6} md={4}>
          <ShowIntercomButton
            size="large"
            fullWidth
            variant="outlined"
            color="primary"
            onError={() => setShowIntercomError(true)}
          >
            <ContactSupport />
            <span className={classes.tagEntryTitle}>
              Still need help? Start a chat
            </span>
          </ShowIntercomButton>
        </Grid>
      </Grid>
    </Page>
  )
}

export default FAQPage

export const getStaticProps: GetStaticProps<FAQPageProps> = async (
  _context,
) => {
  const FAQSeries = await fetchSeries('faq-entries')
  const FAQs = (FAQSeries?.fields.entries || []).filter(isEntry).filter(isFaq)
  const Tags = (await fetchTags()) || []
  return {
    props: {
      FAQs,
      Tags,
    },
    revalidate: 20,
  }
}
